/**
 * IXD 테이블 셀 + 텍스트 블록 컴포넌트 (Composite Pattern)
 *
 * Figma: Design System 0.1 > IXD-DSD Reconciliation Table
 *
 * - IxdTable.DebitCreditChip: 차변/대변 outlined chip
 * - IxdTable.Header: IXD 헤더 셀 (period·isNegated·chipPlacement)
 * - IxdTable.Cell: IXD 본문 셀 (matchResult + Σ indicator)
 * - IxdTable.NarrativeBlock: 텍스트 문단 블록 (matchResult)
 */

import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { DecorateInteraction } from "@/toggle-ui-kit/decorate/interaction/decorate-interaction";
import ChipOutlined from "@/toggle-ui-kit/chip/chip-outlined";
import ChipIcon from "@/toggle-ui-kit/chip/chip-icon";
import { IconChip } from "@/toggle-ui-kit/chip";
import { Minus, Plus } from "lucide-react";
import CellLinkageMarker from "./cell-linkage-marker";
import CellManualEditMarker from "./cell-manual-edit-marker";
import CellNoticeMarker, { type CellNoticeTone } from "./cell-notice-marker";

// ============================================
// 공통 타입
// ============================================

export type MatchResult =
  | "default"
  | "missing-in-ixd"
  | "match"
  | "misplaced-in-ixd"
  | "sign-reversed";

export type Period = "당기" | "전기";
export type ChipPlacement = "right" | "bottom";
export type DebitCreditValue = "차변" | "대변";
export type IxdCellBorderSide = "top" | "right" | "bottom" | "left";

const BORDER_SIDE_CLASS: Record<IxdCellBorderSide, string> = {
  top: "border-t",
  right: "border-r",
  bottom: "border-b",
  left: "border-l",
};

/**
 * 기본 동작: 셀별 right/bottom + tr 내 첫 셀의 left + 첫 행 셀의 top.
 * 결과적으로 어떤 테이블 구성이든 외곽선 + 내부 그리드가 자동으로 완성된다.
 * 모든 테두리 정의가 이 컴포넌트 안에 머무르므로 소비자는 별도 처리 불필요.
 *
 * 주의: `[tr:first-child>&]:border-t` 는 `<thead>` + `<tbody>` 두 섹션이 모두
 * 존재하는 테이블에서 tbody 의 첫 행에도 적용된다. 현재 IXD 테이블은 tbody
 * 단독 구조라 문제 없음. thead 를 함께 쓰는 신규 소비자가 생기면 selector 보강 필요.
 */
const DEFAULT_AUTO_BORDER_CLASSES =
  "border-r border-b first:border-l [tr:first-child>&]:border-t border-line-normal";

const getBorderClasses = (borders: IxdCellBorderSide[]): string => {
  if (borders.length === 0) return "";
  return cn(
    borders.map((side) => BORDER_SIDE_CLASS[side]),
    "border-line-normal",
  );
};

// ============================================
// matchResult → 배경색 매핑
// ============================================

const matchResultBgMap: Record<MatchResult, string> = {
  default: "bg-background-base",
  "missing-in-ixd": "bg-background-base",
  match: "bg-green-200",
  "misplaced-in-ixd": "bg-rose-200",
  "sign-reversed": "bg-amber-200",
};

// ============================================
// diffStatus → 배경색 매핑 (preview 비교 전용)
// ============================================

/**
 * preview 비교(IXD↔IXD)의 셀 상태 색. matchResult(ixd-dsd 대사 의미)와는 의미가
 * 달라 별도 매핑을 둔다. 색은 DSD-DSD 대사(dsd-dsd-compare)와 동일하게 맞춘다
 * (추가=lime, 삭제=red, 변경=indigo).
 */
export type IxdDiffStatus = "unchanged" | "inserted" | "deleted" | "modified";

const diffStatusBgMap: Record<Exclude<IxdDiffStatus, "unchanged">, string> = {
  inserted: "bg-lime-200",
  deleted: "bg-red-200",
  modified: "bg-indigo-200",
};

// ============================================
// DebitCreditChip (차변 / 대변)
// ============================================

export interface IxdDebitCreditChipProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value?: DebitCreditValue;
}

const IxdDebitCreditChip = ({
  value = "차변",
  className,
  ...rest
}: IxdDebitCreditChipProps) => {
  const isCredit = value === "대변";
  return (
    <ChipOutlined
      size="18px"
      rounded={false}
      showRightIcon={false}
      className={cn(
        "bg-background-base",
        isCredit
          ? "border-indigo-300 text-indigo-600"
          : "border-fuchsia-300 text-fuchsia-600",
        className,
      )}
      {...rest}
    >
      {value}
    </ChipOutlined>
  );
};

IxdDebitCreditChip.displayName = "IxdTable.DebitCreditChip";

// ============================================
// IxdHeaderCell
// ============================================

export interface IxdHeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** 기간: 당기(amber) / 전기(slate) */
  period?: Period;
  /** 부호 반전 여부 — true면 sky-500 텍스트 */
  isNegated?: boolean;
  /** 차변/대변 칩 배치 방향 */
  chipPlacement?: ChipPlacement;
  /** 차변/대변 칩 표시 여부 */
  showDebitCreditChip?: boolean;
  /** 차변/대변 값 */
  debitCreditValue?: DebitCreditValue;
  /** line_item_sum 계산 부호 칩 — "+" 또는 "-". th 행 헤더에만 표시. */
  calcWeightSign?: "+" | "-";
  /**
   * preview 비교(ixd-ixd) 셀 상태. 지정 시 period 배경보다 우선하며,
   * "unchanged" 가 아닐 때만 배경을 덮어쓴다.
   */
  diffStatus?: IxdDiffStatus;
  /**
   * 테두리를 적용할 면 — 명시적 override.
   * 미지정 시 right/bottom + 첫 셀 left + 첫 행 top 이 자동 적용되어
   * 테이블 외곽선 + 내부 그리드가 함께 완성된다.
   * `[]` 를 넘기면 테두리 없음.
   */
  borders?: IxdCellBorderSide[];
  children?: React.ReactNode;
  className?: string;
}

const thVariants = cva(
  "box-border relative min-w-[80px] max-w-[200px] p-0 rounded-none before:z-[1]",
  {
    variants: {
      period: {
        당기: "bg-amber-100",
        전기: "bg-slate-100",
      },
    },
  },
);

// forwardRef: TooltipWrapper(asChild) 등이 th 를 트리거 앵커로 쓸 수 있게 한다.
const IxdHeaderCell = forwardRef<HTMLTableCellElement, IxdHeaderCellProps>(
  (
    {
      period = "당기",
      isNegated = false,
      chipPlacement = "right",
      showDebitCreditChip = true,
      debitCreditValue = "차변",
      calcWeightSign,
      diffStatus,
      borders,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const textClass = isNegated ? "text-sky-500" : "text-label-strong";
    const isBottom = chipPlacement === "bottom";
    const borderClasses = borders
      ? getBorderClasses(borders)
      : DEFAULT_AUTO_BORDER_CLASSES;
    const diffBgOverride =
      diffStatus && diffStatus !== "unchanged"
        ? diffStatusBgMap[diffStatus]
        : undefined;

    return (
      <DecorateInteraction asChild variant="strong" color="neutral">
        <th
          ref={ref}
          className={cn(
            thVariants({ period }),
            diffBgOverride,
            borderClasses,
            className,
          )}
          {...rest}
        >
          <div className="flex items-center justify-between gap-1 p-2 caption-2-medium">
            <div
              className={cn(
                "relative flex gap-1",
                isBottom ? "flex-col items-start" : "items-center",
              )}
            >
              <span className={cn("min-w-0", textClass, !isBottom && "flex-1")}>
                {children}
              </span>
              {showDebitCreditChip && (
                <IxdDebitCreditChip value={debitCreditValue} />
              )}
            </div>
            {calcWeightSign && (
              <IconChip
                size="medium"
                className={
                  calcWeightSign === "+"
                    ? "bg-green-200 text-green-700"
                    : "bg-red-200 text-red-700"
                }
              >
                {calcWeightSign === "+" ? <Plus /> : <Minus />}
              </IconChip>
            )}
          </div>
        </th>
      </DecorateInteraction>
    );
  },
);

IxdHeaderCell.displayName = "IxdTable.Header";
// ============================================
// IxdCell
// ============================================

export interface IxdCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** 대사 결과 (ixd-dsd) */
  matchResult?: MatchResult;
  /**
   * preview 비교(ixd-ixd) 셀 상태. 지정 시 matchResult 배경보다 우선하며,
   * "unchanged" 가 아닐 때만 배경을 덮어쓴다.
   */
  diffStatus?: IxdDiffStatus;
  iconChipClassName?: string;
  icon?: React.ReactNode;
  /**
   * 우상단 연동(동일값) 표식 표시 여부. 같은 identity 가 다른 위치에도 있어
   * 클릭 시 linkage popover/modal 이 열리는 셀에 true 로 준다.
   */
  /** 반영하지 않은 셀 표식. 연동·수동수정 표식보다 우선한다 */
  showNoticeMarker?: boolean;
  /** 표식 색. 원인이 다르면 색으로 가른다 */
  noticeTone?: CellNoticeTone;
  showLinkageMarker?: boolean;
  /** 사용자가 직접 수정하거나 붙여넣은 셀의 우상단 주황색 표식 표시 여부 */
  showManualEditMarker?: boolean;
  /**
   * 숫자 셀 우측정렬. icon 칩은 왼쪽에 고정된 채 값만 오른쪽 끝으로 간다
   * (DSD 리뷰 셀 `toggle-ui-kit/dsd-table.tsx` DsdCell 과 동일한 배치).
   */
  alignRight?: boolean;
  /**
   * 테두리를 적용할 면 — 명시적 override.
   * 미지정 시 right/bottom + 첫 셀 left + 첫 행 top 이 자동 적용되어
   * 테이블 외곽선 + 내부 그리드가 함께 완성된다.
   * `[]` 를 넘기면 테두리 없음.
   */
  borders?: IxdCellBorderSide[];
  children?: React.ReactNode;
  className?: string;
}

const cellContainerVariants = cva(
  "relative min-w-[80px] p-0 rounded-none before:z-[1]",
  {
    variants: {
      matchResult: {
        default: `${matchResultBgMap.default}`,
        "missing-in-ixd": `${matchResultBgMap["missing-in-ixd"]}`,
        match: `${matchResultBgMap.match}`,
        "misplaced-in-ixd": `${matchResultBgMap["misplaced-in-ixd"]}`,
        "sign-reversed": `${matchResultBgMap["sign-reversed"]}`,
      },
    },
  },
);

const IxdCell = ({
  matchResult = "default",
  diffStatus,
  icon,
  showNoticeMarker = false,
  noticeTone,
  showLinkageMarker = false,
  showManualEditMarker = false,
  alignRight = false,
  borders,
  children,
  className,
  iconChipClassName,
  ...rest
}: IxdCellProps) => {
  const borderClasses = borders
    ? getBorderClasses(borders)
    : DEFAULT_AUTO_BORDER_CLASSES;
  const diffBgOverride =
    diffStatus && diffStatus !== "unchanged"
      ? diffStatusBgMap[diffStatus]
      : undefined;
  return (
    <DecorateInteraction asChild variant="light" color="neutral">
      <td
        className={cn(
          cellContainerVariants({ matchResult }),
          diffBgOverride,
          borderClasses,
          className,
        )}
        {...rest}
      >
        {showNoticeMarker ? (
          <CellNoticeMarker tone={noticeTone} />
        ) : showLinkageMarker ? (
          <CellLinkageMarker />
        ) : (
          showManualEditMarker && <CellManualEditMarker />
        )}
        <div
          className={cn(
            "flex items-center gap-1.5 rounded-none p-2 caption-2-medium",
            matchResult === "missing-in-ixd"
              ? "text-label-alternative"
              : "text-label-normal",
          )}
        >
          {icon && (
            <ChipIcon
              size="18px"
              className={cn(
                "shrink-0 bg-neutral-800 text-static-white font-semibold",
                iconChipClassName,
              )}
            >
              {icon}
            </ChipIcon>
          )}
          <span className="min-w-0 flex-1">{children}</span>
          {/* 아이콘 자리 대체 spacer. 우측정렬 셀에선 값이 이만큼 안쪽에 멈춰
              아이콘 유무에 따라 우측 끝이 어긋나므로 뺀다. */}
          {!icon && !alignRight && <div className="size-4"></div>}
        </div>
      </td>
    </DecorateInteraction>
  );
};

IxdCell.displayName = "IxdTable.Cell";

// ============================================
// IxdNarrativeBlock
// ============================================

export interface IxdNarrativeBlockProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** 대사 결과 */
  matchResult?: MatchResult;
  children?: React.ReactNode;
  className?: string;
}

const IxdNarrativeBlock = ({
  matchResult = "default",
  children,
  className,
  ...rest
}: IxdNarrativeBlockProps) => {
  const bgClass = matchResultBgMap[matchResult];

  return (
    <div className={cn("relative max-w-[540px]", bgClass, className)} {...rest}>
      <DecorateInteraction asChild variant="light" color="neutral">
        <div className="rounded-none px-2 py-1 caption-2-medium text-label-normal">
          {children}
        </div>
      </DecorateInteraction>
    </div>
  );
};

IxdNarrativeBlock.displayName = "IxdTable.NarrativeBlock";

// ============================================
// Composite Export
// ============================================

export const IxdTable = {
  DebitCreditChip: IxdDebitCreditChip,
  Header: IxdHeaderCell,
  Cell: IxdCell,
  NarrativeBlock: IxdNarrativeBlock,
};

export default IxdTable;
